import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BaseComponent } from '@morningstar/shared/base/base-component';
import { StorageService, StorageTypes } from '@morningstar/shared/base/storage-service';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { Search } from '../search';
import { SearchBarService } from '../search-bar.service';
import { SearchClient } from '../search-client';
import { SearchInvoice } from '../search-invoice';
import { SearchPerson } from '../search-person';
import { ClientAutocompleteService } from '../../autocomplete/client/client-autocomplete.service';

@Component({
  selector: 'wsg-search-bar-dialog',
  templateUrl: './search-bar-dialog.component.html',
  styleUrls: ['./search-bar-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClientAutocompleteService]
})
export class SearchBarDialogComponent extends BaseComponent {

  // == STATE ======================================================

  searchControl = new FormControl();
  search: Search;

  columnWidth = new BehaviorSubject<string>('25%');
  columnWidth$ = this.columnWidth.asObservable();

  // == INTERMEDIATE ===============================================

  searchResults$ = this.searchControl.valueChanges.pipe(
    filter(searchText => !!searchText && searchText.length >= 3),
    debounceTime(600),
    switchMap(searchText => this.searchBarService.getMatches(searchText)),
    tap(search => {
      this.search = search;
      this.calculateColumnWidth(search);
    })
  );

  // == LIFECYCLE ==================================================

  constructor(private searchBarService: SearchBarService,
              private router: Router,
              private storageService: StorageService,
              private autocompleteService: ClientAutocompleteService,
              public dialog: MatDialogRef<SearchBarDialogComponent>) {
    super(searchBarService);
  }

  // == HELPERS ====================================================

  getSearchResultSection(route: string): string {
    let formattedSection = '';
    const splitRoute = route.split('/');

    for (let i = 1; i < splitRoute.length; i++) {
      let splitSubRoute = splitRoute[i].split('-');

      splitSubRoute.forEach((element, index) =>
        splitSubRoute[index] = element[0].toUpperCase() + element.substr(1)
      );

      const formattedSubRoute = splitSubRoute.join(' ');

      if (i > 1) {
        formattedSection += ' => ';
      }
      formattedSection += `${formattedSubRoute[0].toUpperCase()}${formattedSubRoute.substr(1)}`
    }

    return formattedSection;
  }

  navigateToRoute(route: string) {
    this.searchBarService.saveSearch({
      ...this.search,
      route
    });

    this.dialog.close();
    this.router.navigate([route]);
  }

  navigateToClient(searchClient: SearchClient) {
    const currentLocation = this.storageService.location.value;
    const isActive = this.storageService.clientListIsActive.value;
    const isFutureClient = this.storageService.futureClients.value;

    if (currentLocation !== searchClient.location) {
      this.storageService.setItem(StorageTypes.location, searchClient.location);
    }

    if(isActive !== searchClient.active) {
      this.autocompleteService.setIsActive(searchClient.active, searchClient.tenantId);
    } else if(currentLocation !== searchClient.location) {
      this.autocompleteService.getList(searchClient.location, searchClient.tenantId);
    }

    if (isFutureClient !== (searchClient.tenantType === "FC")) {
      this.autocompleteService.setFutureClients(searchClient.tenantType === "FC", searchClient.tenantId);
    }

    const route = '/client/general-info';

    this.searchBarService.saveSearch({
      ...this.search,
      route
    });

    this.dialog.close();
    this.router.navigate([route], { queryParams: { tenantId: searchClient.tenantId }});
  }

  navigateToPerson(searchPerson: SearchPerson) {
    this.storageService.setItem(StorageTypes.location, searchPerson.location);

    const route = '/client/people';

    this.searchBarService.saveSearch({
      ...this.search,
      route
    });

    this.dialog.close();
    this.router.navigate([route], {
      queryParams: {
        tenantId: searchPerson.tenantId,
        pseq: searchPerson.pSeq
      }
    });
  }

  navigateToInvoice(searchInvoice: SearchInvoice) {
    this.storageService.setItem(StorageTypes.location, searchInvoice.location);

    const route = '/client/account-info/invoices';

    this.searchBarService.saveSearch({
      ...this.search,
      route
    });

    this.dialog.close();
    this.router.navigate([route], {
      queryParams: {
        tenantId: searchInvoice.tenantId,
        invoiceNumber: searchInvoice.invoiceNumber
      }
    });
  }

  calculateColumnWidth(search: Search) {

    let numberOfColumns = 0;

    if (search.routes.length > 0) {
      numberOfColumns++;
    }

    if (search.clients.length > 0) {
      numberOfColumns++;
    }

    if (search.people.length > 0) {
      numberOfColumns++;
    }

    if (search.invoices.length > 0) {
      numberOfColumns++;
    }

    this.columnWidth.next(`${100/numberOfColumns}%`);
  }
}
